import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CalculatorIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { canViewCosts, normalizeRole } from '../../utils/roles';

// Lightweight interfaces for dashboard data (subset of full types)
interface DashboardVehicle {
  id: string;
  status: string;
  asking_price?: number;
  total_cost?: number;
  year: number;
  make: string;
  model: string;
  created_at?: string;
}

interface DashboardCustomer {
  id: string;
  first_name: string;
  last_name: string;
  created_at?: string;
}

interface DashboardDeal {
  id: string;
  deal_status: string;
  created_at: string;
}

interface StatItem {
  name: string;
  stat: string;
  icon: React.ElementType;
  change: string;
  changeType: 'increase' | 'decrease' | 'neutral';
  color?: string;
  link?: string;
  secondary?: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

interface QuickAction {
  name: string;
  description: string;
  href: string;
  icon: React.ElementType;
  color: string;
}

interface RecentActivity {
  id: string;
  type: 'customer' | 'vehicle' | 'deal';
  message: string;
  timestamp: string;
  status: 'success' | 'warning' | 'info';
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const { user } = useAuth();
  const isDeveloper = normalizeRole(user?.role) === 'developer';

  const baseQuickActions: QuickAction[] = [
    {
      name: 'Deal Calculator',
      description: 'Calculate deal structure & payments',
      href: '/calculator',
      icon: CalculatorIcon,
      color: 'bg-indigo-500 hover:bg-indigo-600',
    },
    {
      name: 'Add Customer',
      description: 'Create a new customer record',
      href: '/customers',
      icon: UserGroupIcon,
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      name: 'Add Vehicle',
      description: 'Add vehicle to inventory',
      href: '/vehicles',
      icon: TruckIcon,
      color: 'bg-green-500 hover:bg-green-600',
    },
    {
      name: 'Create Deal',
      description: 'Start a new deal',
      href: '/deals',
      icon: CurrencyDollarIcon,
      color: 'bg-purple-500 hover:bg-purple-600',
    },
  ];
  const quickActions: QuickAction[] = isDeveloper
    ? [
        ...baseQuickActions,
        {
          name: 'SuperTracker',
          description: 'Monitor tasks & milestones',
          href: '/tracker',
          icon: ArrowTrendingUpIcon,
          color: 'bg-orange-500 hover:bg-orange-600',
        },
      ]
    : baseQuickActions;

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch data from all endpoints
      const [customersData, vehiclesData, dealsData] = await Promise.all([
        apiService.get<DashboardCustomer[]>('/customers/').catch((): DashboardCustomer[] => []),
        apiService.get<DashboardVehicle[]>('/vehicles/').catch((): DashboardVehicle[] => []),
        apiService.get<DashboardDeal[]>('/deals/').catch((): DashboardDeal[] => []),
      ]);

      // Safely convert to arrays
      const customers = Array.isArray(customersData) ? customersData : [];
      const vehicles = Array.isArray(vehiclesData) ? vehiclesData : [];
      const deals = Array.isArray(dealsData) ? dealsData : [];

      // Calculate statistics
      const availableVehicles = vehicles.filter((v) => v.status === 'available').length;
      const pendingVehicles = vehicles.filter((v) => v.status === 'pending').length;
      let totalListingValue = 0;
      let totalCostBasis = 0;
      if (vehicles && vehicles.length > 0) {
        totalListingValue = vehicles.reduce((sum: number, v) => sum + (v.asking_price || 0), 0);
        totalCostBasis = vehicles.reduce((sum: number, v: any) => sum + (v.total_cost || 0), 0);
      }

      // Calculate monthly revenue from active deals
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const monthlyDeals = deals.filter((deal) => {
        const dealDate = new Date(deal.created_at);
        return (
          dealDate.getMonth() === currentMonth &&
          dealDate.getFullYear() === currentYear &&
          deal.deal_status !== 'CANCELLED'
        );
      });

      // Count active deals (not cancelled)
      const activeDeals = deals.filter((deal) => deal.deal_status !== 'CANCELLED').length;

      // Update stats
      setStats([
        {
          name: 'Total Customers',
          stat: customers.length.toString(),
          icon: UserGroupIcon,
          change: customers.length > 0 ? '+12%' : 'New',
          changeType: customers.length > 0 ? 'increase' : 'neutral',
          color: 'text-blue-600',
          link: '/customers',
        },
        {
          name: 'Available Vehicles',
          stat: `${availableVehicles}/${vehicles.length}`,
          icon: TruckIcon,
          change: vehicles.length > 0 ? `${pendingVehicles} pending` : 'Import needed',
          changeType: availableVehicles > 0 ? 'increase' : 'neutral',
          color: 'text-green-600',
          link: '/vehicles',
        },
        {
          name: 'Inventory Snapshot',
          stat: fmt(totalListingValue),
          secondary: canViewCosts(user?.role)
            ? `Cost basis: ${fmt(totalCostBasis)} · Projected gross: ${fmt(totalListingValue - totalCostBasis)}`
            : undefined,
          icon: BanknotesIcon,
          change: vehicles.length > 0 ? `${vehicles.length} vehicles` : '$0',
          changeType: totalListingValue > 0 ? 'increase' : 'neutral',
          color: 'text-purple-600',
          link: canViewCosts(user?.role) ? '/inventory/costs' : '/vehicles',
        },
        {
          name: 'Active Deals',
          stat: activeDeals.toString(),
          icon: ChartBarIcon,
          change:
            monthlyDeals.length > 0 ? `${monthlyDeals.length} this month` : 'Create first deal',
          changeType: activeDeals > 0 ? 'increase' : 'neutral',
          color: 'text-orange-600',
          link: '/deals',
        },
      ]);

      // Generate recent activity
      const activities: RecentActivity[] = [];

      // Add recent vehicles
      if (vehicles && vehicles.length > 0) {
        vehicles.slice(0, 2).forEach((vehicle) => {
          activities.push({
            id: `vehicle-${vehicle.id}`,
            type: 'vehicle',
            message: `Added ${vehicle.year} ${vehicle.make} ${vehicle.model} to inventory`,
            timestamp: vehicle.created_at || new Date().toISOString(),
            status: 'success',
          });
        });
      }

      // Add recent customers
      if (customers && customers.length > 0) {
        customers.slice(0, 2).forEach((customer) => {
          activities.push({
            id: `customer-${customer.id}`,
            type: 'customer',
            message: `New customer: ${customer.first_name} ${customer.last_name}`,
            timestamp: customer.created_at || new Date().toISOString(),
            status: 'info',
          });
        });
      }

      // Sort by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentActivity(activities.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default stats on error
      setStats([
        {
          name: 'Total Customers',
          stat: '0',
          icon: UserGroupIcon,
          change: 'Start here',
          changeType: 'neutral',
          color: 'text-blue-600',
          link: '/customers',
        },
        {
          name: 'Vehicle Inventory',
          stat: '0',
          icon: TruckIcon,
          change: 'Import vehicles',
          changeType: 'neutral',
          color: 'text-green-600',
          link: '/vehicles',
        },
        {
          name: 'Inventory Snapshot',
          stat: '$0',
          icon: BanknotesIcon,
          change: 'Add inventory',
          changeType: 'neutral',
          color: 'text-purple-600',
          link: '/vehicles',
        },
        {
          name: 'Active Deals',
          stat: '0',
          icon: ChartBarIcon,
          change: 'Create deal',
          changeType: 'neutral',
          color: 'text-orange-600',
          link: '/deals',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Welcome to SimpleDealer. Here's an overview of your dealership.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {loading
          ? // Loading skeleton
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 bg-gray-300 rounded"></div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-8 bg-gray-300 rounded"></div>
                  </div>
                </div>
              </div>
            ))
          : stats.map((item) => {
              const StatCard = item.link ? Link : 'div';
              return (
                <StatCard
                  key={item.name}
                  to={item.link || ''}
                  className={`card ${item.link ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''}`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <item.icon className={`h-6 w-6 ${item.color || 'text-gray-400'}`} />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">{item.stat}</div>
                          <div className="ml-2 flex items-baseline text-sm font-semibold">
                            <span
                              className={
                                item.changeType === 'increase'
                                  ? 'text-green-600'
                                  : item.changeType === 'decrease'
                                    ? 'text-red-600'
                                    : 'text-gray-500'
                              }
                            >
                              {item.change}
                            </span>
                          </div>
                        </dd>
                        {item.secondary && (
                          <p className="text-xs text-gray-500 mt-1">{item.secondary}</p>
                        )}
                      </dl>
                    </div>
                  </div>
                </StatCard>
              );
            })}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`flex-shrink-0 p-3 rounded-lg ${action.color}`}>
                  <action.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-900">{action.name}</h3>
                  <p className="text-xs text-gray-500">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Content Grid - 50/50 Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Welcome & Getting Started - 50% width */}
        <div>
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Welcome to CarLotPilot</h3>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600">
                You're now using CarLotPilot, a modern BHPH dealership management system designed to
                replace expensive, bloated solutions like DealerCenter.
              </p>

              {/* Feature highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Core Features:</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                      Vehicle Inventory Management
                    </li>
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                      Customer Relationship Management
                    </li>
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                      Deal Management & Tracking
                    </li>
                    {isDeveloper && (
                      <li className="flex items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                        SuperTracker - Task Management
                      </li>
                    )}
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Recent Updates:</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-blue-500 mr-2" />
                      VIN-based vehicle URLs
                    </li>
                    <li className="flex items-center">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-blue-500 mr-2" />
                      Enhanced image galleries
                    </li>
                    <li className="flex items-center">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-blue-500 mr-2" />
                      Multi-tenant architecture
                    </li>
                    <li className="flex items-center">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-blue-500 mr-2" />
                      Responsive design system
                    </li>
                  </ul>
                </div>
              </div>

              {/* Next Steps */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-3">Next Steps:</h4>
                <div className="flex flex-wrap gap-2">
                  <Link to="/customers" className="btn-primary">
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Add Customer
                  </Link>
                  <Link to="/vehicles" className="btn-secondary">
                    <TruckIcon className="h-4 w-4 mr-2" />
                    Import Vehicles
                  </Link>
                  {isDeveloper && (
                    <Link to="/tracker" className="btn-secondary">
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-2" />
                      Try SuperTracker
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Recent Activity & System Status - 50% width */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            </div>
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {activity.status === 'success' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                      {activity.status === 'warning' && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                      )}
                      {activity.status === 'info' && (
                        <ClockIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>

          {/* System Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">System Status</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="badge badge-success">Connected</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Server</span>
                  <span className="badge badge-success">Running</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Image Storage</span>
                  <span className="badge badge-success">Ready</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email Service</span>
                  <span className="badge badge-success">Active</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">SMS Service</span>
                  <span className="badge badge-success">Active</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500">
                  All core systems operational. Ready for production use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
